import { useForm } from "react-hook-form";
import { Form } from "../../../ui/common/form";
import { TooltipProvider } from "../../../ui/common/tooltip";
import { FormInputNumber } from "../../../ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../ui/Input/FormSelector/FormSelect";
import { VERSION_DETAILS_COPY } from "../constants/versionDetails.constants";
import { VersionDetailsDraft, VersionDetailsFormValues } from "../types/versionHistory.types";
import { validateNumberOfUnits, validatePricePerUnit } from "../utils/versionDetails.utils";
import { InlineEditActions } from "./InlineEditActions";

interface VersionDetailsFormProps {
  initialValues: VersionDetailsFormValues;
  yearOptions: number[];
  currencyCode: string;
  versionLabel: string;
  isSaving: boolean;
  /** Only versions with at least one saved field show the delete button -- nothing to clear otherwise. */
  canDelete: boolean;
  /** Resolves to an error message to surface, or null on success. */
  onSubmit: (draft: VersionDetailsDraft) => Promise<string | null>;
  onCancel: () => void;
  onRequestDelete: () => void;
}

/**
 * Owns the draft values for one version's details via its own FormProvider -- the codebase's
 * row-level inline-edit convention (see VersionNameCell). Keeping the draft here means typing
 * never re-renders the surrounding table.
 *
 * `defaultValues` are seeded once on mount and deliberately never re-synced from props: this is
 * an explicit-save editor, so a background refetch must not overwrite what the user is typing.
 */
export const VersionDetailsForm = ({
  initialValues,
  yearOptions,
  currencyCode,
  versionLabel,
  isSaving,
  canDelete,
  onSubmit,
  onCancel,
  onRequestDelete
}: VersionDetailsFormProps) => {
  const form = useForm<VersionDetailsFormValues>({ defaultValues: initialValues });

  const handleSave = form.handleSubmit(async values => {
    const errorMessage = await onSubmit(values);
    if (errorMessage) {
      // The backend doesn't say which field it's rejecting (e.g. a reporting-year conflict, not
      // necessarily numberOfUnits), so this is a form-level error rather than pinned to one input.
      form.setError("root", { type: "manual", message: errorMessage });
    }
  });

  const rootError = form.formState.errors.root?.message;

  return (
    <Form {...form}>
      <div
        className="tw-flex tw-flex-wrap tw-items-start tw-gap-4"
        onKeyDown={event => {
          if (event.key === "Escape") onCancel();
        }}
      >
        <div className="tw-w-[130px]">
          <FormSelect
            control={form.control}
            name="reportingYear"
            label={VERSION_DETAILS_COPY.yearLabel}
            placeholder={VERSION_DETAILS_COPY.yearPlaceholder}
            options={yearOptions.map(year => ({ value: year, label: String(year) }))}
            disabled={isSaving}
            className="tw-text-sm tw-font-mono"
          />
        </div>
        <div className="tw-w-[160px]">
          <FormInputNumber
            control={form.control}
            name="numberOfUnits"
            label={VERSION_DETAILS_COPY.unitsLabel}
            placeholder={VERSION_DETAILS_COPY.unitsPlaceholder}
            removeDecimal
            formatWithCommas
            disabled={isSaving}
            aria-label={VERSION_DETAILS_COPY.unitsAriaLabel(versionLabel)}
            className="tw-text-sm tw-font-mono"
            rules={{ validate: validateNumberOfUnits }}
          />
        </div>
        <div className="tw-w-[160px]">
          <FormInputNumber
            control={form.control}
            name="pricePerUnit"
            label={VERSION_DETAILS_COPY.priceLabel}
            placeholder={VERSION_DETAILS_COPY.pricePlaceholder}
            enableLimitedDecimal
            rightContentEnable={!!currencyCode}
            rightContent={currencyCode}
            disabled={isSaving}
            aria-label={VERSION_DETAILS_COPY.priceAriaLabel(versionLabel)}
            className="tw-text-sm tw-font-mono"
            rules={{ validate: validatePricePerUnit }}
          />
        </div>
        {/* pt-6 lines the buttons up with the inputs, clearing the fields' labels. */}
        <div className="tw-pt-6">
          <TooltipProvider delayDuration={200}>
            <InlineEditActions
              isSaving={isSaving}
              variant="bordered"
              size="md"
              saveLabel={VERSION_DETAILS_COPY.saveAriaLabel(versionLabel)}
              cancelLabel={VERSION_DETAILS_COPY.cancelAriaLabel(versionLabel)}
              onSave={handleSave}
              onCancel={onCancel}
              onDelete={canDelete ? onRequestDelete : undefined}
              deleteLabel={VERSION_DETAILS_COPY.deleteAriaLabel(versionLabel)}
            />
          </TooltipProvider>
        </div>
      </div>
      {rootError ? (
        <p className="tw-mt-2 tw-text-sm tw-font-medium tw-text-destructive">{rootError}</p>
      ) : null}
    </Form>
  );
};
